import React, { ReactNode, useContext, useRef } from 'react'
import { createRoot } from 'react-dom/client'
import { IcaAlertMessage } from '../components/IcaElements/IcaAlertMessage'
import { useDeviceContext } from '../contexts/device-context'
import './alert-context.scss'

export interface IAlertContext {
  alert: {
    success: (title: string, displayinseconds?: number) => void
    error: (title: string, displayinseconds?: number) => void
    info: (title: string, displayinseconds?: number) => void
    warning: (title: string, displayinseconds?: number) => void
  }
}

const AlertContext = React.createContext<IAlertContext | undefined>(undefined)

export function AlertProvider({ children }: { children: ReactNode }) {
  const { deviceState } = useDeviceContext()
  const alertContainerRef = useRef<HTMLDivElement | null>(null)

  function createAlert(
    type: 'success' | 'failure' | 'info' | 'warning',
    title: string,
    displayinseconds?: number,
  ) {
    if (!alertContainerRef.current) {
      return
    }

    const container = document.createElement('div')
    alertContainerRef.current.appendChild(container)

    const root = createRoot(container)

    const handleClose = () => {
      root.unmount()
      container.remove()
    }

    root.render(
      <IcaAlertMessage
        alarmtype={type}
        title={title}
        displayinseconds={displayinseconds || 3}
        onClose={handleClose}
      />
    )
  }

  const state: IAlertContext = React.useMemo(() => {
    return {
      alert: {
        success: (title: string, displayinseconds?: number) => {
          createAlert('success', title, displayinseconds)
        },
        error: (title: string, displayinseconds?: number) => {
          createAlert('failure', title, displayinseconds)
        },
        info: (title: string, displayinseconds?: number) => {
          createAlert('info', title, displayinseconds)
        },
        warning: (title: string, displayinseconds?: number) => {
          createAlert('warning', title, displayinseconds)
        },
      },
    }
  }, [])

  return (
    <AlertContext.Provider value={state}>
      <div
        ref={alertContainerRef}
        className={`alert-container${deviceState.isMobile ? '-mobile' : ''}`}
      />
      {children}
    </AlertContext.Provider>
  )
}

export function useAlertContext() {
  const context = useContext(AlertContext)
  if (!context) {
    throw new Error('useAlertContext must be used within a AlertProvider')
  }
  return context
}
